import { ExcelFileViewer } from "@/components/common/ExcelViewer/ExcelFileViewer"
import { LoadingSpinner } from "@/components/common/LoadingSpinner/LoadingSpinner"
import { ModalBase } from "@/components/common/ModalBase/ModalBase"
import { toast } from "@/components/common/Toast"
import { Typography } from "@/components/common/Typography/Typography"
import {
  createDocumentTagMenuEntries,
  DocumentTagBadge,
} from "@/components/feature/KnowledgebaseFilters/DocumentTagCategoryMenu"
import { Button } from "@/components/ui/button"
import { useFeatureFlag } from "@/contexts/FeatureFlagContext"
import { useGetDocumentTags } from "@/hooks/useKnowledgeBaseCategories/useKnowledgeBaseCategories"
import { useTranslation } from "@/hooks/useTranslation/useTranslation"
import {
  knowledgebaseApiCalls,
  useGetKnowledgebaseFile,
} from "@/services/api/knowledgebaseService"
import { Citation } from "@/types/questionnaire/models"
import { downloadFile } from "@/utils/downloadUtils"
import { isExcelFileName } from "@/utils/fileUtils"
import { IconLayoutSidebarRight, IconX } from "@tabler/icons-react"
import { useCallback, useEffect, useMemo, useState } from "react"
import { useNavigate } from "react-router"
import { AddTagButton } from "../../KnowledgebaseFileListItem/AddTagButton"

interface FilePreviewModalProps {
  urlQueryId?: string
  citation?: Citation
  fullScreen?: boolean
  /** When true, positions the modal to leave space for a right-side sheet (500px) */
  sidebarOpen?: boolean
  setIsPreviewModalOpen: (isOpen: boolean) => void
  /** When provided, shows a "View Details" button that opens the tag/details panel */
  onViewDetails?: () => void
}

export const FilePreviewModal: React.FC<FilePreviewModalProps> = ({
  urlQueryId,
  citation,
  setIsPreviewModalOpen,
  fullScreen = false,
  sidebarOpen = false,
  onViewDetails,
}) => {
  const { translate } = useTranslation()
  const navigate = useNavigate()
  const kbTaggingFlowEnabled = useFeatureFlag("KB_TAGGING_FLOW")
  const [excelPreviewUrl, setExcelPreviewUrl] = useState<string>()
  const [isLoadingContent, setIsLoadingContent] = useState(false)

  const {
    isPending: isFetchFilePending,
    isError: isFetchFileError,
    data: fileData,
  } = useGetKnowledgebaseFile(urlQueryId)
  const { data: documentTags } = useGetDocumentTags(
    urlQueryId,
    kbTaggingFlowEnabled && !!urlQueryId,
  )

  const customTagEntries = useMemo(() => {
    if (!documentTags?.categories) {
      return []
    }

    return createDocumentTagMenuEntries(documentTags.categories).filter(
      (entry) => entry.tag.source === "user",
    )
  }, [documentTags])

  const closePreviewModal = useCallback(() => {
    const searchParams = new URLSearchParams(window.location.search)
    searchParams.delete("file")
    navigate(`${window.location.pathname}?${searchParams.toString()}`, {
      replace: true,
    })
    setIsPreviewModalOpen(false)
  }, [navigate, setIsPreviewModalOpen])

  useEffect(() => {
    if (isFetchFileError) {
      toast.error(translate("KNOWLEDGEBASE/PREVIEW_FILE_ERROR"))
      closePreviewModal()
    }
    if (!isFetchFileError && fileData && !isFetchFilePending) {
      const isExcelPreview = isExcelFileName(fileData.fileName)

      if (fileData?.mimetype !== "application/pdf" && !isExcelPreview) {
        if (fileData?.url) {
          downloadFile(fileData.url, fileData.fileName)
        }
        closePreviewModal()
        toast.success(translate("KNOWLEDGEBASE/DOWNLOAD_FILE_SUCCESS"))
      }
    }
  }, [
    citation,
    closePreviewModal,
    fileData,
    isFetchFileError,
    isFetchFilePending,
    translate,
  ])

  const isExcelPreview = useMemo(
    () => !!fileData?.fileName && isExcelFileName(fileData.fileName),
    [fileData?.fileName],
  )

  useEffect(() => {
    if (
      !urlQueryId ||
      !fileData ||
      isFetchFilePending ||
      isFetchFileError ||
      !isExcelPreview
    ) {
      setExcelPreviewUrl(undefined)
      setIsLoadingContent(false)
      return
    }

    let isCancelled = false
    setIsLoadingContent(true)
    setExcelPreviewUrl(undefined)

    knowledgebaseApiCalls
      .getFileContent(urlQueryId)
      .then((arrayBuffer) => {
        if (isCancelled) {
          return
        }

        const blob = new Blob([arrayBuffer], {
          type:
            fileData.mimetype ||
            "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        })
        const blobUrl = URL.createObjectURL(blob)
        setExcelPreviewUrl(blobUrl)
      })
      .catch(() => {
        if (isCancelled) {
          return
        }

        toast.error(translate("KNOWLEDGEBASE/PREVIEW_FILE_ERROR"))
        closePreviewModal()
      })
      .finally(() => {
        if (!isCancelled) {
          setIsLoadingContent(false)
        }
      })

    return () => {
      isCancelled = true
    }
  }, [
    closePreviewModal,
    fileData,
    isExcelPreview,
    isFetchFileError,
    isFetchFilePending,
    translate,
    urlQueryId,
  ])

  useEffect(() => {
    return () => {
      if (excelPreviewUrl?.startsWith("blob:")) {
        URL.revokeObjectURL(excelPreviewUrl)
      }
    }
  }, [excelPreviewUrl])

  const previewUrl = isExcelPreview
    ? excelPreviewUrl
    : fileData && fileData.mimetype === "application/pdf"
      ? fileData.url
      : undefined

  return (
    <ModalBase showSidebarLeft fullScreen={fullScreen} compact={sidebarOpen}>
      <div
        data-testid="knowledgebase-file-preview-modal"
        className="flex h-full flex-col"
      >
        <div className="mb-4 flex items-center border-b border-grey4 py-2">
          <div className="flex-1">
            {citation ? (
              <Typography variant="textXS" className="px-4 text-grey5">
                {translate("QUESTIONNAIRE_ANSWERS/CITATION_TITLE")}:{" "}
                {citation?.sourceFileName} /{" "}
                {translate("QUESTIONNAIRE_ANSWERS/CITATION_PAGE_ABBREVIATION")}
                {citation?.sourceFilePage}
              </Typography>
            ) : null}

            {kbTaggingFlowEnabled && fileData && (
              <div className="flex flex-wrap items-start justify-between gap-4">
                <div className="flex min-w-0 flex-1 flex-col gap-2">
                  {customTagEntries.length > 0 ? (
                    <div className="flex flex-wrap items-center gap-1">
                      {customTagEntries.map((entry) => (
                        <DocumentTagBadge key={entry.tag.id} entry={entry} />
                      ))}
                      <AddTagButton onClick={() => onViewDetails?.()} />
                    </div>
                  ) : null}
                </div>
              </div>
            )}
          </div>

          <div className="flex items-center gap-1">
            {onViewDetails && kbTaggingFlowEnabled && fileData && (
              <Button
                variant="outline"
                size="sm"
                className="gap-1.5"
                onClick={onViewDetails}
              >
                <IconLayoutSidebarRight size={14} />
                {translate("KNOWLEDGEBASE/FILE_DETAILS_TITLE")}
              </Button>
            )}
            <Button
              variant="ghost"
              onClick={closePreviewModal}
              data-testid="knowledgebase-file-preview-close"
              aria-label={translate("CLOSE")}
            >
              <IconX />
            </Button>
          </div>
        </div>

        <div className="flex min-h-0 flex-1 flex-col">
          {!isFetchFilePending && !isLoadingContent && previewUrl ? (
            isExcelPreview ? (
              <div className="h-full w-full overflow-hidden">
                <ExcelFileViewer file={previewUrl} />
              </div>
            ) : (
              <iframe
                src={
                  citation?.sourceFilePage
                    ? `${previewUrl}#page=${citation.sourceFilePage}`
                    : previewUrl
                }
                title={translate("KNOWLEDGEBASE/PDF_VIEWER_IFRAME_TITLE")}
                className="h-full w-full flex-1"
              >
                {translate("PDF_VIEW_NOT_SUPPORTED")}
              </iframe>
            )
          ) : (
            <div className="flex h-full items-center justify-center">
              <LoadingSpinner />
            </div>
          )}
        </div>
      </div>
    </ModalBase>
  )
}
